import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';

export const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-gray-900 sm:text-6xl mb-6">
            Commission <span className="text-blue-600">Custom Software</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Tell us what you need, get a quote in 24 hours, and watch your custom software come to life.
            No technical knowledge required.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => navigate('/wizard')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
            >
              Start Your Project
            </Button>
            <Button
              onClick={() => navigate('/about')}
              variant="outline"
              className="px-8 py-4 text-lg border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              Learn More
            </Button>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Describe Your Project</h3>
                <p className="text-gray-600">
                  Answer 8 simple questions about what you need. Our wizard guides you through features,
                  design, timeline, and budget.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-cyan-200">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-cyan-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Your Quote</h3>
                <p className="text-gray-600">
                  Receive an instant estimate and a detailed quote within 24 hours. Review features,
                  pricing, and timeline before approving.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Watch It Build</h3>
                <p className="text-gray-600">
                  Track progress in real-time, test on staging, request changes, and launch when ready.
                  Full support included.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tech Showcase */}
        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Built With Modern Tech</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'React', description: 'Modern UI' },
              { name: 'Node.js', description: 'Scalable backend' },
              { name: 'PostgreSQL', description: 'Reliable data' },
              { name: 'TypeScript', description: 'Type safety' },
              { name: 'AI/ML', description: 'Smart features' },
              { name: 'Cloud Hosting', description: 'Always online' },
              { name: 'Mobile Ready', description: 'Works everywhere' },
              { name: 'Secure', description: 'Enterprise-grade' },
            ].map((tech) => (
              <Card key={tech.name} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6 text-center">
                  <h4 className="font-semibold text-gray-900">{tech.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{tech.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Transparent Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                <p className="text-gray-600 mb-4">Simple web apps and MVPs</p>
                <p className="text-4xl font-bold text-blue-600 mb-6">
                  R5K - R15K
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>✓ Basic features</li>
                  <li>✓ Responsive design</li>
                  <li>✓ 1-2 week delivery</li>
                  <li>✓ Hosting included (1 month)</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-4 border-blue-600 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Professional</h3>
                <p className="text-gray-600 mb-4">Full-featured applications</p>
                <p className="text-4xl font-bold text-blue-600 mb-6">
                  R15K - R50K
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>✓ Advanced features</li>
                  <li>✓ User authentication</li>
                  <li>✓ Admin panel</li>
                  <li>✓ API integrations</li>
                  <li>✓ 1 month delivery</li>
                  <li>✓ 3 months hosting</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                <p className="text-gray-600 mb-4">Complex systems</p>
                <p className="text-4xl font-bold text-blue-600 mb-6">
                  R50K+
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>✓ Custom architecture</li>
                  <li>✓ Scalable infrastructure</li>
                  <li>✓ AI/ML integration</li>
                  <li>✓ Real-time features</li>
                  <li>✓ Priority support</li>
                  <li>✓ 12 months hosting</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Your custom software is just 8 questions away.
          </p>
          <Button
            onClick={() => navigate('/wizard')}
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
          >
            Start Building Now
          </Button>
        </div>
      </div>
    </div>
  );
};
